import React, {
  useState, useEffect, useCallback, useRef, useReducer,
} from 'react';
import { Box, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';

// ── Sub-modules ───────────────────────────────────────────────────────────────
import { CANVAS_W, CANVAS_H, getEngBreakpoint, getEngCanvasSize, resolveEngHotspot, resolveEngBg } from './constants';
import { reducer, INITIAL_STATE, fetchLobby, preloadLobbyAssets, preloadVideo, preloadImage } from './lobbyReducer';
import { EngineHeader, EngMobileDrawerOverlay } from './header';
import { FloatingChatManager } from './ChatComponents';
import { VideoModal, IframeModal, GalleryModal } from './ModalComponents';
import { HotspotSVGLayer, EngTextElement, EngImageElement } from './HotspotLayer';
import { EngExhibitorGrid } from './ExhibitorGrid';
import { EngLivestreamChat } from './LivestreamChat';
import { EngStreamElement, IntroOverlay, TransitionOverlay } from './Overlays';




// ── useContainSize ────────────────────────────────────────────
// Computes canvas box dimensions preserving aspect ratio inside the wrapper.
//
// offsetTop   — px consumed above the canvas (mobile header). Canvas is sized
//              and positioned within the remaining area (availH − offsetTop).
//              Absolute top from wrapper origin = offsetTop + containSize.top.
//
// Sizing mode (mutually exclusive, checked in order):
//   coverWidth  — MOBILE: always fill 100% width, derive height from aspect.
//                Canvas may overflow bottom; wrapper clips it. Anchored at top
//                of remaining area (top = 0). Maximises screen coverage.
//   fillWidth   — TABLET-PORTRAIT: prefer 100% width, but if the derived height
//                would exceed availH, fall back to height-constrained scale so
//                nothing is ever cut off. Always centred both axes.
//   (neither)   — TABLET-LANDSCAPE / DESKTOP: standard contain — largest rect
//                that fits without overflow, centred both axes.
function useContainSize(wrapperRef, targetW = CANVAS_W, targetH = CANVAS_H, offsetTop = 0, fillWidth = false, coverWidth = false) {
  const [size, setSize] = useState({ width: 0, height: 0, top: 0, left: 0, scale: 1 });
  useEffect(() => {
    if (!wrapperRef.current) return;
    const compute = () => {
      const el = wrapperRef.current;
      if (!el) return;
      const availW = el.clientWidth;
      const availH = el.clientHeight - offsetTop;
      if (!availW || availH <= 0) return;

      const scaleByW = availW / targetW;
      const scaleByH = availH / targetH;

      if (coverWidth) {
        // Mobile: always fill 100% width, overflow vertically (clipped by wrapper).
        // Canvas is anchored to the top of the remaining area; no vertical centering
        // so no black gap appears above or below.
        const scale  = scaleByW;
        const width  = availW;
        const height = Math.round(targetH * scale);
        setSize({ width, height, scale, top: 0, left: 0 });
      } else if (fillWidth) {
        // Tablet-portrait: prefer width-fill; fall back to height-fill if it
        // would overflow so content is fully visible. Centre both axes.
        const scale  = scaleByW * targetH <= availH ? scaleByW : scaleByH;
        const width  = Math.round(targetW * scale);
        const height = Math.round(targetH * scale);
        setSize({
          width, height, scale,
          top:  Math.round((availH - height) / 2),
          left: Math.round((availW - width)  / 2),
        });
      } else {
        // Tablet-landscape / desktop: standard contain, centred both axes.
        const scale  = Math.min(scaleByW, scaleByH);
        const width  = Math.round(targetW * scale);
        const height = Math.round(targetH * scale);
        setSize({
          width, height, scale,
          top:  Math.round((availH - height) / 2),
          left: Math.round((availW - width)  / 2),
        });
      }
    };
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(wrapperRef.current);
    return () => ro.disconnect();
  // eslint-disable-next-line
  }, [wrapperRef, targetW, targetH, offsetTop, fillWidth, coverWidth]);
  return size;
} 
// ── useEngBreakpoint ──────────────────────────────────────────────────────────
// Tracks the active responsive breakpoint based on the wrapper element's width.
// Matches the 4 breakpoints defined in the builder.
function useEngBreakpoint(wrapperRef) {
  const [bp, setBp] = useState('desktop');
  useEffect(() => {
    if (!wrapperRef.current) return;
    const compute = () => {
      const w = wrapperRef.current?.clientWidth || window.innerWidth;
      setBp(getEngBreakpoint(w));
    };
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(wrapperRef.current);
    return () => ro.disconnect();
  }, [wrapperRef]);
  return bp;
}
 
// Extract every downloadable asset URL from a single lobby's raw API data.
function collectLobbyAssets(lobbyData) {
  const lobby = lobbyData.lobby || lobbyData; // handle both shapes
  const urls  = new Set();
 
  // Background
  if (lobby.bgUrl)         urls.add({ url: lobby.bgUrl,         type: lobby.bgType === 'video' ? 'video' : 'image' });
  // Intro video
  if (lobby.introVideoUrl) urls.add({ url: lobby.introVideoUrl, type: 'video' });
 
  // Hotspot-level assets (transition videos, gallery images, video hotspots)
  // Include carousel slide hotspots + elements so their images are preloaded.
  const _slideItems = (lobby.slides || []).flatMap(s => [...(s.hotspots || []), ...(s.elements || [])]);
  const hotspots = [...(lobby.hotspots || []), ...(lobby.elements || []), ..._slideItems];
  for (const h of hotspots) {
    const hType = h.hotspotType || h.elementType; // elements use elementType
    const meta = h.meta || {};
    const cfg  = h.config || {};
    // Transition video on lobby-link hotspots
    if (meta.transitionVideoUrl) urls.add({ url: meta.transitionVideoUrl, type: 'video' });
    // Stream element bg / video
    if (cfg.streamUrl && cfg.streamType === 'video') urls.add({ url: cfg.streamUrl, type: 'video' });
    // Direct video hotspot
    if (h.action?.type === 'url' && meta._isVideo && h.action.url) urls.add({ url: h.action.url, type: 'video' });
    // Gallery images
    if (meta.galleryImages) {
      for (const img of meta.galleryImages) {
        if (img.url) urls.add({ url: img.url, type: 'image' });
      }
    }
    // Image element/hotspot
    if ((hType === 'image' || meta._isImage) && meta.imageUrl) urls.add({ url: meta.imageUrl, type: 'image' });
    // Exhibitor booth thumbnails / logos are loaded on-demand; skip here.
  }
 
  // Deduplicate by URL string
  const seen = new Set();
  const list = [];
  for (const asset of urls) {
    if (!seen.has(asset.url)) { seen.add(asset.url); list.push(asset); }
  }
  return list;
}
 
// Download up to `concurrency` assets at a time, calling onProgress(0‥1) after each.
async function downloadQueue(assets, onProgress, concurrency = 4) {
  if (!assets.length) { onProgress(1); return; }
  let completed = 0;
  const total   = assets.length;
  const tick    = () => { completed++; onProgress(completed / total); };
 
  // Split into chunks of `concurrency`
  for (let i = 0; i < assets.length; i += concurrency) {
    const batch = assets.slice(i, i + concurrency);
    await Promise.all(batch.map(a => {
      const p = a.type === 'video' ? preloadVideo(a.url) : preloadImage(a.url);
      return p.then(tick).catch(tick); // never block on a single failure
    }));
  }
}
 
// ── GlobalLoadingScreen ───────────────────────────────────────────────────────
function GlobalLoadingScreen({ logoUrl, progress, visible }) {
  // Fade out once progress reaches 1
  const [opacity, setOpacity] = useState(1);
  const prevVisible = useRef(visible);
 
  useEffect(() => {
    if (prevVisible.current && !visible) setOpacity(0);
    prevVisible.current = visible;
  }, [visible]);
 
  const pct = Math.round((progress || 0) * 100);
 
  return (
    <Box
      sx={{
        position: 'absolute', inset: 0, zIndex: 9000,
        background: '#0a0e1a',
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        opacity,
        transition: opacity === 0 ? 'opacity 0.6s ease' : 'none',
        pointerEvents: opacity === 0 ? 'none' : 'all',
      }}
    >
      {/* Event logo */}
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', px: 4 }}>
        {logoUrl ? (
          <Box
            component="img"
            src={logoUrl}
            alt="Event logo"
            sx={{
              maxWidth: '280px',
              maxHeight: '160px',
              width: 'auto',
              height: 'auto',
              objectFit: 'contain',
              filter: 'drop-shadow(0 4px 24px rgba(0,0,0,0.5))',
              opacity: 0.95,
            }}
          />
        ) : (
          // Fallback pulsing dot if no logo
          <Box sx={{
            width: 56, height: 56, borderRadius: '50%',
            background: 'rgba(16,117,186,0.25)',
            border: '2px solid rgba(16,117,186,0.4)',
            animation: 'eng-logo-pulse 1.8s ease-in-out infinite',
          }} />
        )}
      </Box>
 
      {/* Progress section pinned to the bottom */}
      <Box sx={{ width: '100%', px: '10%', pb: '6%', flexShrink: 0 }}>
        {/* Label */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography sx={{
            fontSize: '0.7rem', fontWeight: 600,
            color: 'rgba(255,255,255,0.4)',
            letterSpacing: '0.1em', textTransform: 'uppercase',
          }}>
            Loading your experience
          </Typography>
          <Typography sx={{
            fontSize: '0.7rem', fontWeight: 700,
            color: 'rgba(255,255,255,0.35)',
            fontVariantNumeric: 'tabular-nums',
          }}>
            {pct}%
          </Typography>
        </Box>
 
        {/* Track */}
        <Box sx={{
          width: '100%', height: 3,
          background: 'rgba(255,255,255,0.08)',
          borderRadius: 99,
          overflow: 'hidden',
        }}>
          {/* Fill */}
          <Box sx={{
            height: '100%',
            width: `${pct}%`,
            borderRadius: 99,
            background: 'linear-gradient(90deg, #1075BA, #3faee0)',
            boxShadow: '0 0 10px rgba(16,117,186,0.7)',
            transition: 'width 0.25s ease',
          }} />
        </Box>
      </Box>
 
      <style>{`
        @keyframes eng-logo-pulse {
          0%, 100% { transform: scale(1);   opacity: 0.7; }
          50%       { transform: scale(1.08); opacity: 1;   }
        }
      `}</style>
    </Box>
  );
}
 
// ── LobbyFadeOverlay ──────────────────────────────────────────────────────────
// Fades the current lobby out to black, calls onCommit (new lobby snaps in
// behind — bg already cached), then fades black back out to reveal it.
function LobbyFadeOverlay({ onCommit, onDone }) {
  const overlayRef = useRef(null);
 
  useEffect(() => {
    let rafId;
    const FADE_OUT = 380; // ms — current lobby fades to black
    const FADE_IN  = 420; // ms — new lobby fades in from black
 
    const startOut = performance.now();
 
    const tickOut = (now) => {
      const t = Math.min((now - startOut) / FADE_OUT, 1);
      const ease = 1 - Math.pow(1 - t, 2); // ease-out quad
      if (overlayRef.current) overlayRef.current.style.opacity = String(ease);
      if (t < 1) {
        rafId = requestAnimationFrame(tickOut);
      } else {
        // Full black — commit the new lobby
        onCommit?.();
        // Brief hold so the new lobby has one frame to paint
        setTimeout(() => {
          const startIn = performance.now();
          const tickIn = (now2) => {
            const t2 = Math.min((now2 - startIn) / FADE_IN, 1);
            const ease2 = Math.pow(t2, 2); // ease-in quad → ease out opacity
            if (overlayRef.current) overlayRef.current.style.opacity = String(1 - ease2);
            if (t2 < 1) {
              rafId = requestAnimationFrame(tickIn);
            } else {
              onDone?.();
            }
          };
          rafId = requestAnimationFrame(tickIn);
        }, 32); // ~2 frames
      }
    };
 
    rafId = requestAnimationFrame(tickOut);
    return () => cancelAnimationFrame(rafId);
  }, []); // eslint-disable-line
 
  return (
    <div
      ref={overlayRef}
      style={{
        position: 'absolute', inset: 0,
        zIndex: 7000,
        background: '#000',
        opacity: 0,
        pointerEvents: 'all',
      }}
    />
  );
}
 
// ── LobbyEngine ───────────────────────────────────────────────────────────────
export function LobbyEngine({
  startLobbyId,
  previewAuthorName = 'Preview Admin',
  participantName,
  onLogout,
  onHotspotClick,
  style,
  className,
  breakpoint: breakpointOverride,
}) {
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);
  const [bgLoaded,            setBgLoaded]            = useState(false);
  const [activeModal,         setActiveModal]         = useState(null);
  const [activeTransitionUrl, setActiveTransitionUrl] = useState(null);
 
  const pendingTransitionRef = useRef(null);
  const pendingLobbyDataRef  = useRef(null);
 
  // ── Global preload state ──────────────────────────────────────────────────
  // 'idle'      → not started yet
  // 'fetching'  → fetching lobby manifests
  // 'loading'   → downloading asset queue
  // 'done'      → all assets downloaded, engine is live
  // 'error'     → initial fetch failed
  const [preloadPhase,    setPreloadPhase]    = useState('idle');
  const [preloadProgress, setPreloadProgress] = useState(0);
  const [eventLogo,       setEventLogo]       = useState(null);
  const [preloadError,    setPreloadError]    = useState(null);
 
  // ── Mobile drawer state (owned here so overlay renders inside wrapperRef) ──
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [openChats,      setOpenChats]      = useState([]);
  const [minimizedChats, setMinimizedChats] = useState({});
 
  const anonId = useRef(
    (() => {
      try {
        const key = 'govirtual_preview_anon_id';
        let id    = sessionStorage.getItem(key);
        if (!id) { id = 'preview_' + Math.random().toString(36).slice(2); sessionStorage.setItem(key, id); }
        return id;
      } catch { return 'preview_anon'; }
    })()
  );
 
  const openChatWindow = useCallback((hotspot) => {
    const id = hotspot.id || hotspot._id;
    setOpenChats(prev => {
      if (prev.find(c => c.id === id)) return prev;
      return [...prev, {
        id,
        channelId: hotspot.hotspotAction?.channelId || null,
        title:     hotspot.hotspotAction?.chatTitle || hotspot.label || 'Live Chat',
        mode:      hotspot.hotspotAction?.mode || 'group',
      }];
    });
    setMinimizedChats(prev => ({ ...prev, [id]: false }));
  }, []);
 
  const closeChatWindow = useCallback((id) => setOpenChats(prev => prev.filter(c => c.id !== id)), []);
  const toggleMinimize  = useCallback((id) => setMinimizedChats(prev => ({ ...prev, [id]: !prev[id] })), []);
 
  // ── Contain sizing ────────────────────────────────────────────────────────
  const wrapperRef         = useRef(null);
  const canvasBoxRef       = useRef(null);
  const mobileHeaderRef    = useRef(null);
  const [mobileHeaderH, setMobileHeaderH] = useState(0);

  // ── Responsive breakpoint ─────────────────────────────────────────────────
  // When a breakpointOverride is supplied (e.g. from the builder preview),
  // use it directly so the engine respects the selected device mode even when
  // the wrapper's CSS pixel width doesn't match (due to transform: scale).
  const detectedBp = useEngBreakpoint(wrapperRef);
  const engBp    = breakpointOverride || detectedBp;
  const isMobile = engBp === 'mobile';
  const isTablet = engBp === 'tablet-portrait' || engBp === 'tablet-landscape';

  // Pick the canvas dimensions matching the active breakpoint so the contain
  // scale uses the correct aspect ratio (e.g. 375×700 for mobile, 768×1024
  // for tablet-portrait, 1024×768 for tablet-landscape, 1440×810 for desktop).
  const { canvasW: bpCanvasW, canvasH: bpCanvasH } = getEngCanvasSize(engBp);

  // Mobile (coverWidth): always fill 100% width, anchor canvas to top, clip overflow
  // bottom — maximises screen coverage with no side gutters.
  // Tablet-portrait (fillWidth): prefer full width; fall back to height-fit when
  // the derived height would exceed the available area, then centre both axes.
  // Tablet-landscape / desktop: standard contain, centred.
  // Both mobile and tablet-portrait: prefer full-width scale; if that would
  // overflow vertically, fall back to height-constrained scale. Always centred.
  // Tablet-landscape / desktop: standard contain, centred.
  const coverWidth   = false;
  const fillWidth    = isMobile || engBp === 'tablet-portrait';
  // The mobile header is position:absolute (overlay), so it does not consume
  // vertical space. Pass offsetTop=0 so the canvas uses the full wrapper height.
  const containSize  = useContainSize(wrapperRef, bpCanvasW, bpCanvasH, 0, fillWidth, coverWidth);
  const canvasScale  = containSize.scale || 1;
  const canvasReady  = containSize.width > 0 && containSize.height > 0;

  // When the breakpoint changes, only reset bgLoaded if the effective bg URL
  // actually differs at the new bp (different responsive override). If the same
  // URL is reused, the cached image fires onLoad/naturalWidth instantly so we
  // must NOT reset bgLoaded — doing so causes a permanent fade-out.
  const prevBgUrlRef = useRef(null);
  // useEffect(() => {
  //   if (!lobby) return;
  //   return console.log(lobby)
  //   const { bgUrl } = resolveEngBg(lobby, engBp);
  //   if (bgUrl !== prevBgUrlRef.current) {
  //     setBgLoaded(false);
  //     prevBgUrlRef.current = bgUrl;
  //   }
  // }, [engBp, lobby]);

  // Measure mobile header height so the canvas can be pushed below it
  useEffect(() => {
    if (!isMobile || !mobileHeaderRef.current) { setMobileHeaderH(0); return; }
    const ro = new ResizeObserver(([e]) => setMobileHeaderH(e.contentRect.height));
    ro.observe(mobileHeaderRef.current);
    setMobileHeaderH(mobileHeaderRef.current.offsetHeight);
    return () => ro.disconnect();
  }, [isMobile]);
  const loadLobby = useCallback(async (lobbyId, pushHistory = false, preloadedData = null) => {
    if (!lobbyId) return;
    setActiveModal(null);
 
    const transitionUrl = pendingTransitionRef.current;
    pendingTransitionRef.current = null;
 
    try {
      let data = preloadedData;
 
      if (transitionUrl) {
        if (data) {
          pendingLobbyDataRef.current = {
            data, pushHistory, lobbyId,
            onCommit: () => {
              const isVideoBg = data.lobby.bgType === 'video';
              // For carousel lobbies the root bgUrl is empty; bg is on each slide.
      // Treat carousel as bgLoaded=true initially so hotspots render; the
      // slide bg image will fade in via its own onLoad handler.
      const _isCarousel1 = data.lobby?.lobbyType === 'carousel';
      setBgLoaded(_isCarousel1 || (!isVideoBg && !!data.lobby.bgUrl));
              dispatch({ type: 'LOAD_SUCCESS', payload: { ...data, pushHistory } });
              window.parent?.postMessage({ type: 'LOBBY_LOADED', lobbyId, lobbyName: data.lobby.name }, '*');
              pendingLobbyDataRef.current = null;
            },
          };
          setActiveTransitionUrl(transitionUrl);
        }
        return;
      }
 
      dispatch({ type: 'LOAD_START' });
      data = await fetchLobby(lobbyId);
      dispatch({ type: 'PRELOAD_START' });
      await preloadLobbyAssets(data, progress => dispatch({ type: 'PRELOAD_PROGRESS', payload: progress }));
 
      const isVideoBg = data.lobby.bgType === 'video';
      // For carousel lobbies the root bgUrl is empty; bg is on each slide.
      // Treat carousel as bgLoaded=true initially so hotspots render; the
      // slide bg image will fade in via its own onLoad handler.
      const _isCarousel1 = data.lobby?.lobbyType === 'carousel';
      setBgLoaded(_isCarousel1 || (!isVideoBg && !!data.lobby.bgUrl));
      dispatch({ type: 'LOAD_SUCCESS', payload: { ...data, pushHistory } });
      window.parent?.postMessage({ type: 'LOBBY_LOADED', lobbyId, lobbyName: data.lobby.name }, '*');
    } catch (err) {
      dispatch({ type: 'LOAD_ERROR', payload: err.message });
      window.parent?.postMessage({ type: 'LOBBY_ERROR', lobbyId, error: err.message }, '*');
    }
  }, []);
 
  // ── stagedLobbyRef ────────────────────────────────────────────────────────
  // Holds fetched lobby data that has been loaded in the background but not
  // yet committed to React state. Committed by commitStagedLobby().
  const stagedLobbyRef = useRef(null);

  // Ref flag set by goBackLobby so commitStagedLobby knows not to push history
  const goingBackRef = useRef(false);

  const commitStagedLobby = useCallback(() => {
    const staged = stagedLobbyRef.current;
    if (!staged) return;
    stagedLobbyRef.current = null;
    const isVideoBg = staged.lobby.bgType === 'video';
    // bgLoaded: images are already in browser cache so onLoad fires instantly.
    // Videos: set false so the <video> onCanPlay in the render handles it.
    // Carousel: root bgUrl is empty; treat as loaded so hotspots render immediately.
    const _isStagedCarousel = staged.lobby?.lobbyType === 'carousel';
    setBgLoaded(_isStagedCarousel || (!isVideoBg && !!staged.lobby.bgUrl));
    // goingBackRef is set by goBackLobby — don't push history when going back
    const pushHistory = !goingBackRef.current;
    goingBackRef.current = false;
    dispatch({ type: 'LOAD_SUCCESS', payload: { ...staged, pushHistory } });
    window.parent?.postMessage({ type: 'LOBBY_LOADED', lobbyId: staged.lobby._id, lobbyName: staged.lobby.name }, '*');
  }, []);
 
  // ── Lobby-to-lobby crossfade ──────────────────────────────────────────────
  const [lobbyFading, setLobbyFading] = useState(false);
  const handleLobbyFadeDone = useCallback(() => setLobbyFading(false), []);
 
  // ── navigateToLobby ───────────────────────────────────────────────────────
  //
  // transitionVideoUrl present → old video-transition flow (pendingTransitionRef)
  // no transition video        → stage bg silently, crossfade when ready
  // backgroundLoad = true      → used by ExhibitorGrid: stage bg, return
  //                              Promise<void> that resolves when bg is cached.
  //                              Does NOT trigger any visual change — the caller
  //                              (BoothTransitionOverlay) controls the reveal.
  // ── Shared bg-stage helper ───────────────────────────────────────────────
  // Fetches lobby JSON, stores in stagedLobbyRef, preloads bg asset.
  // Returns a Promise<void> that resolves when the bg is ready to show.
  const stageAndPreloadLobby = useCallback((targetLobbyId) => {
    let resolveReady;
    const ready = new Promise(res => { resolveReady = res; });

    fetchLobby(targetLobbyId).then(data => {
      stagedLobbyRef.current = data;
      const bgUrl     = data.lobby.bgUrl  || null;
      const isVideoBg = data.lobby.bgType === 'video';

      if (!bgUrl) { resolveReady(); return; }

      if (isVideoBg) {
        const v = document.createElement('video');
        v.preload = 'metadata'; v.muted = true; v.crossOrigin = 'anonymous';
        const done = () => resolveReady();
        v.onloadedmetadata = done; v.oncanplay = done; v.onerror = done;
        setTimeout(done, 5000);
        v.src = bgUrl;
      } else {
        const img = new Image();
        img.onload = img.onerror = () => resolveReady();
        img.src = bgUrl;
      }
    }).catch(() => {
      stagedLobbyRef.current = null;
      resolveReady();
    });

    return ready;
  }, []);

  const navigateToLobby = useCallback((targetLobbyId, transitionVideoUrl = null, backgroundLoad = false) => {
    setLivestreamFullscreen(false); // always collapse livestream on lobby navigation
    window.history.pushState({ lobbyId: targetLobbyId }, '', window.location.href);

    const stageAndPreload = () => stageAndPreloadLobby(targetLobbyId);
 
    // ── ExhibitorGrid background-load path ────────────────────────────────
    // Just preload, return promise. Visual change is the overlay's responsibility.
    if (backgroundLoad) {
      return stageAndPreload();
    }
 
    // ── Transition video path ─────────────────────────────────────────────
    // Preload both the video and the bg asset in parallel, then play the
    // old video-transition overlay.
    if (transitionVideoUrl) {
      dispatch({ type: 'PRELOAD_START' });
      const fetchPromise      = fetchLobby(targetLobbyId);
      const transitionPromise = preloadVideo(transitionVideoUrl);
      fetchPromise.then(data => {
        pendingLobbyDataRef.current = data;
        const bgAsset = data.lobby.bgUrl
          ? (data.lobby.bgType === 'video' ? preloadVideo(data.lobby.bgUrl) : preloadImage(data.lobby.bgUrl))
          : Promise.resolve();
        let done = 0; const total = 2;
        const tick = () => { done++; dispatch({ type: 'PRELOAD_PROGRESS', payload: done / total }); };
        Promise.all([bgAsset.then(tick), transitionPromise.then(tick)]).then(() => {
          pendingTransitionRef.current = transitionVideoUrl;
          loadLobby(targetLobbyId, true, pendingLobbyDataRef.current);
        });
      }).catch(err => dispatch({ type: 'LOAD_ERROR', payload: err.message }));
      return Promise.resolve();
    }
 
    // ── No-transition path: crossfade ─────────────────────────────────────
    // Stage + preload bg, then trigger the black crossfade overlay.
    // The overlay fades to black, calls commitStagedLobby (lobby snaps in
    // because bg is already cached), then fades back out to reveal it.
    stageAndPreload().then(() => setLobbyFading(true));
    return Promise.resolve();
  }, [loadLobby, stageAndPreloadLobby]);
 
  // ── Global asset preload (runs once on mount) ─────────────────────────────
  useEffect(() => {
    if (!startLobbyId) return;
    let cancelled = false;
 
    async function run() {
      setPreloadPhase('fetching');
      setPreloadProgress(0);
 
      try {
        // 1. Fetch the start lobby — this gives us event info + all sibling lobbies
        const startData = await fetchLobby(startLobbyId);
        if (cancelled) return;
 
        // 2. Extract event logo for the loading screen
        const logo = startData.event?.logoUrl || startData.event?.logo || null;
        setEventLogo(logo);
 
        // 3. Collect assets from the start lobby itself
        const allAssets = collectLobbyAssets(startData);
 
        // 4. Fetch every sibling lobby to collect their assets too.
        //    We run these fetches concurrently (they're lightweight JSON).
        const siblings = startData.siblings || [];
        const siblingFetches = siblings
          .filter(s => String(s._id) !== String(startLobbyId))
          .map(s => fetchLobby(String(s._id)).catch(() => null)); // ignore individual failures
 
        const siblingDatas = await Promise.all(siblingFetches);
        if (cancelled) return;
 
        for (const sd of siblingDatas) {
          if (sd) allAssets.push(...collectLobbyAssets(sd));
        }
 
        // 5. Deduplicate across all lobbies
        const seen    = new Set();
        const queue   = [];
        for (const asset of allAssets) {
          if (asset.url && !seen.has(asset.url)) {
            seen.add(asset.url);
            queue.push(asset);
          }
        }
 
        // 6. Download the queue with progress updates
        setPreloadPhase('loading');
        await downloadQueue(queue, (p) => {
          if (!cancelled) setPreloadProgress(p);
        });
        if (cancelled) return;
 
        // 7. Kick off the actual first lobby load
        setPreloadProgress(1);
        // Small delay so the bar visually reaches 100% before fading
        await new Promise(r => setTimeout(r, 400));
        if (cancelled) return;
 
        setPreloadPhase('done');
 
        // Now load the start lobby into the engine state
        const isVideoBg = startData.lobby.bgType === 'video';
        const _isCarousel3 = startData.lobby?.lobbyType === 'carousel';
        setBgLoaded(_isCarousel3 || (!isVideoBg && !!startData.lobby.bgUrl));
        dispatch({ type: 'LOAD_SUCCESS', payload: { ...startData, pushHistory: false } });
        window.parent?.postMessage({ type: 'LOBBY_LOADED', lobbyId: startLobbyId, lobbyName: startData.lobby.name }, '*');
 
      } catch (err) {
        if (!cancelled) {
          setPreloadPhase('error');
          setPreloadError(err.message);
        }
      }
    }
 
    run();
    return () => { cancelled = true; };
  }, [startLobbyId]); // eslint-disable-line 
 
  // ── postMessage bridge ────────────────────────────────────────────────────
  useEffect(() => {
    const handler = e => { if (e.data?.type === 'LOAD_LOBBY' && e.data.lobbyId) loadLobby(e.data.lobbyId, false); };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [loadLobby]);
 
  // ── Browser back button ───────────────────────────────────────────────────
  useEffect(() => {
    const handler = () => {
      const history = state.history || [];
      const prev    = history[history.length - 1];
      if (prev) navigateToLobby(prev, null);
    };
    window.addEventListener('popstate', handler);
    return () => window.removeEventListener('popstate', handler);
  }, [state.history, navigateToLobby]);
 
  // ── Hotspot click handler ─────────────────────────────────────────────────
  const handleHotspotClick = useCallback((hotspot) => {
    const action = hotspot.hotspotAction;
    if (!action) return;
    window.parent?.postMessage({ type: 'HOTSPOT_CLICK', hotspot }, '*');
 
    const defaultHandler = () => {
      switch (hotspot.hotspotType) {
        case 'link':
          if (action.linkTarget === 'external' && action.externalUrl) {
            const url = /^https?:\/\//i.test(action.externalUrl) ? action.externalUrl : `https://${action.externalUrl}`;
            window.open(url, action.openIn === 'blank' ? '_blank' : '_self');
          } else if (action.targetId) {
            navigateToLobby(action.targetId, action.transitionVideoUrl || null);
          }
          break;
        case 'image': {
          // Image element with a link action — navigate same as link hotspot
          const imgAction = hotspot.hotspotAction || {};
          if (imgAction.linkTarget === 'external' && imgAction.externalUrl) {
            window.open(imgAction.externalUrl, '_blank', 'noopener,noreferrer');
          } else if (imgAction.targetId) {
            navigateToLobby(imgAction.targetId, imgAction.transitionVideoUrl || null);
          }
          return;
        }
        case 'video':   setActiveModal({ type: 'video',   hotspot }); break;
        case 'iframe':  setActiveModal({ type: 'iframe',  hotspot }); break;
        case 'gallery': setActiveModal({ type: 'gallery', hotspot }); break;
        case 'chat':    openChatWindow(hotspot); break;
        default: break;
      }
    };
 
    onHotspotClick ? onHotspotClick(hotspot, defaultHandler) : defaultHandler();
  }, [onHotspotClick, navigateToLobby, loadLobby, openChatWindow]);
 
  // ── Clickability predicate ────────────────────────────────────────────────
  const isClickable = h => {
    if (!h.isActive) return false;
    if (h.hotspotType === 'link')    return !!(h.hotspotAction?.targetId || h.hotspotAction?.externalUrl);
    if (h.hotspotType === 'video')   return !!h.hotspotAction?.videoUrl;
    if (h.hotspotType === 'iframe')  return !!h.hotspotAction?.embedCode;
    if (h.hotspotType === 'gallery') return !!(h.hotspotAction?.images?.length);
    if (h.hotspotType === 'chat')    return !!h.hotspotAction?.channelId;
    return false;
  };
 
  const closeModal           = useCallback(() => setActiveModal(null), []);
  const handleTransitionDone = useCallback(() => { setActiveTransitionUrl(null); dispatch({ type: 'TRANSITION_DONE' }); }, []);
  const handleIntroDone      = useCallback(() => dispatch({ type: 'INTRO_DONE' }), []);
 
  const { status, lobby, event, hotspots, introVideoUrl, loadProgress } = state;
  const lobbyHeaderConfig = event?.lobbyHeader || null;

  // Update favicon whenever the event branding changes
  useEffect(() => {
    if (event?.branding?.logoUrl) {
      let link = document.querySelector("link[rel='icon']");
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
      }
      link.href = event.branding.logoUrl;
    }
    return () => {
      const link = document.querySelector("link[rel='icon']");
      if (link) link.href = '/favicon.ico';
    };
  }, [event?.branding?.logoUrl]);

  // Update document title whenever the active lobby or event changes
  useEffect(() => {
    if (lobby?.name && event?.name) {
      document.title = `${lobby.name} | ${event.name}`;
    } else if (event?.name) {
      document.title = event.name;
    }
    return () => { document.title = ''; };
  }, [lobby?.name, event?.name]);

  // ── Carousel & responsive bg / hotspot resolution ─────────────────────
  // Must be after state destructure since they depend on `lobby` and `hotspots`.
  const isCarousel = lobby?.lobbyType === 'carousel';
  const slides     = isCarousel ? (lobby?.slides || []) : [];

  // Carousel slide state
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);
  const [slideTransition, setSlideTransition]   = useState(null);
  const slideTransitionTimer = useRef(null);
  const slideDragRef         = useRef({ active: false, startX: 0, dx: 0, peekIndex: null });
  const slideElTrackRef      = useRef(null);
  const slideBgTrackRef      = useRef(null);

  // Reset to slide 0 whenever the loaded lobby changes
  const lastLobbyIdRef = useRef(null);
  useEffect(() => {
    if (!lobby?._id) return;
    if (lobby._id !== lastLobbyIdRef.current) {
      lastLobbyIdRef.current = lobby._id;
      setActiveSlideIndex(0);
      setSlideTransition(null);
      if (slideTransitionTimer.current) clearTimeout(slideTransitionTimer.current);
    }
  }, [lobby?._id]); // eslint-disable-line

  // When the active slide changes in a carousel, do NOT reset bgLoaded.
  // The incoming slide's bg was already rendered (and thus cached by the browser)
  // inside the sliding track during the transition — resetting bgLoaded here would
  // cause the static elements layer to briefly disappear (flicker) while waiting
  // for an onLoad event that fires instantly for a cached image.
  // bgLoaded stays true throughout so the handoff from track → static is seamless.

  const navigateToSlide = useCallback((toIndex, direction = 'right') => {
    if (slideTransitionTimer.current) clearTimeout(slideTransitionTimer.current);
    // Clear any drag inline style so the CSS sx transition takes over.
    if (slideBgTrackRef.current) { slideBgTrackRef.current.style.transform = ''; slideBgTrackRef.current.style.transition = ''; }
    // setSlideTransition truthy -> CSS transition enabled on film-strip (via sx)
    // setActiveSlideIndex -> film-strip translateX updates to new position
    setSlideTransition({ toIndex, direction, phase: 'animate' });
    setActiveSlideIndex(toIndex);
    slideTransitionTimer.current = setTimeout(() => {
      setSlideTransition(null);
    }, 420);
  }, []);

  const setTrackTransition = useCallback((t) => {
    if (slideBgTrackRef.current) slideBgTrackRef.current.style.transition = t;
  }, []);
  const setTrackX = useCallback((pct) => {
    if (slideBgTrackRef.current) slideBgTrackRef.current.style.transform = `translateX(${pct}%)`;
  }, []);
  const onSlideDragStart = useCallback((clientX) => {
    if (!isCarousel || slides.length <= 1 || slideTransition) return;
    setTrackTransition('none');
    slideDragRef.current = { active: true, startX: clientX, dx: 0, peekIndex: null };
  }, [isCarousel, slides, slideTransition, setTrackTransition]);
  const onSlideDragMove = useCallback((clientX) => {
    const drag = slideDragRef.current;
    if (!drag.active) return;
    const canvasW = canvasBoxRef.current?.clientWidth || 1;
    const dx = clientX - drag.startX;
    drag.dx = dx;
    const wantPeek = dx < 0
      ? (activeSlideIndex < slides.length - 1 ? activeSlideIndex + 1 : null)
      : (activeSlideIndex > 0 ? activeSlideIndex - 1 : null);
    if (wantPeek !== drag.peekIndex) {
      drag.peekIndex = wantPeek;
      setSlideTransition(wantPeek !== null
        ? { toIndex: wantPeek, direction: dx < 0 ? 'right' : 'left', phase: 'drag' }
        : null);
    }
    if (wantPeek === null) return;
    const numSlides = slides.length || 1;
    const restPct   = -(activeSlideIndex / numSlides) * 100;
    const dragPct   = (dx / canvasW) * (100 / numSlides);
    setTrackX(restPct + dragPct);
  }, [activeSlideIndex, slides, setTrackX]);
  const onSlideDragEnd = useCallback((clientX) => {
    const drag = slideDragRef.current;
    if (!drag.active) return;
    drag.active = false;
    const dx = clientX - drag.startX;
    const canvasW = canvasBoxRef.current?.clientWidth || 1;
    const threshold = canvasW * 0.2;
    const peekIndex = drag.peekIndex;
    drag.peekIndex = null;
    setTrackTransition('transform 0.32s cubic-bezier(0.4,0,0.2,1)');
    if (Math.abs(dx) > threshold && peekIndex !== null) {
      const numSlides = slides.length || 1;
      setTrackX(-(peekIndex / numSlides) * 100);
      if (slideTransitionTimer.current) clearTimeout(slideTransitionTimer.current);
      slideTransitionTimer.current = setTimeout(() => {
        setActiveSlideIndex(peekIndex);
        setSlideTransition(null);
        setTrackTransition('none');
        // Clear inline style - CSS sx transform takes over at new activeSlideIndex
        if (slideBgTrackRef.current) slideBgTrackRef.current.style.transform = '';
      }, 320);
    } else {
      if (peekIndex !== null) {
        // Valid peek but below threshold: spring back to current slide position.
        const numSlides = slides.length || 1;
        setTrackX(-(activeSlideIndex / numSlides) * 100);
        if (slideTransitionTimer.current) clearTimeout(slideTransitionTimer.current);
        slideTransitionTimer.current = setTimeout(() => {
          setSlideTransition(null);
          setTrackTransition('none');
          if (slideBgTrackRef.current) slideBgTrackRef.current.style.transform = '';
        }, 320);
      } else {
        // No adjacent slide (edge): clear inline styles immediately.
        setTrackTransition('none');
        if (slideBgTrackRef.current) slideBgTrackRef.current.style.transform = '';
        setSlideTransition(null);
      }
    }
  }, [activeSlideIndex, slides, navigateToSlide, setTrackX]);

  // Per-slide bg resolution respecting breakpoint overrides
  const resolveSlideBg = (slide) => {
    if (!slide) return { bgUrl: null, bgType: 'image' };
    if (!engBp || engBp === 'desktop') return { bgUrl: slide.bgUrl || null, bgType: slide.bgType || 'image' };
    const bpBg = slide.responsive?.[engBp] || {};
    return {
      bgUrl:  bpBg.bgUrl  !== undefined ? bpBg.bgUrl  : (slide.bgUrl  || null),
      bgType: bpBg.bgType !== undefined ? bpBg.bgType : (slide.bgType || 'image'),
    };
  };

  const activeSlide   = isCarousel ? (slides[activeSlideIndex] ?? null) : null;
  const incomingSlide = isCarousel && slideTransition ? (slides[slideTransition.toIndex] ?? null) : null;

  const effectiveBg = isCarousel ? resolveSlideBg(activeSlide) : resolveEngBg(lobby, engBp);

  // Two-panel sliding background track during a carousel transition
  const slideTrack = (isCarousel && slideTransition) ? (() => {
    const cur = resolveSlideBg(activeSlide);
    const inc = resolveSlideBg(incomingSlide);
    return {
      currentBgUrl:  cur.bgUrl,  currentBgType:  cur.bgType,
      incomingBgUrl: inc.bgUrl,  incomingBgType: inc.bgType,
      direction: slideTransition.direction,
      phase:     slideTransition.phase,
    };
  })() : null;

  // Slide hotspots and elements are already deserialized by the reducer
  // (deserializeHotspot/deserializeElement in lobbyReducer.js LOAD_SUCCESS).
  // resolveEngHotspot just applies per-breakpoint geometry overrides on top.
  const resolveSlideHotspots = (slide) =>
    ((slide?.hotspots || []).concat(slide?.elements || []))
      .map(h => resolveEngHotspot(h, engBp));

  const resolvedHotspots = React.useMemo(
    () => isCarousel ? resolveSlideHotspots(activeSlide) : (hotspots || []).map(h => resolveEngHotspot(h, engBp)),
    // eslint-disable-next-line
    [hotspots, engBp, isCarousel, activeSlide]
  );
  const resolvedIncomingHotspots = React.useMemo(
    () => (isCarousel && incomingSlide) ? resolveSlideHotspots(incomingSlide) : null,
    // eslint-disable-next-line
    [isCarousel, incomingSlide, engBp]
  );

  // Back button — booth lobbies go back to previous lobby; carousel slides go back to slide 0
  const isBoothLobby      = lobby?.lobbyType === 'booth';
  const prevLobbyId       = state.history?.[state.history.length - 1] || null;
  const prevLobby         = state.siblings?.find(l => String(l._id) === String(prevLobbyId)) || null;
  const startLobbyObj     = state.siblings?.find(l => String(l._id) === String(startLobbyId)) || null;
  const isOnNonFirstSlide = isCarousel && activeSlideIndex > 0;
  const isOnStartLobby    = String(lobby?._id) === String(startLobbyId);

  // Destination for back: prefer history stack, fall back to startLobby
  const backDestId   = prevLobbyId || startLobbyId;
  const backDestName = prevLobby?.name || startLobbyObj?.name || 'Lobby';

  const backLabel = isOnNonFirstSlide
    ? `Back to ${slides[0]?.label || 'Exhibition Hall'}`
    : `Back to ${backDestName}`;

  // Pop-back navigation: pop history stack, stage + fade to destination.
  // Uses the same crossfade overlay as forward navigation but commits with
  // pushHistory:false so the destination isn't re-added to the stack.
  // goingBackRef is declared before commitStagedLobby so it can be read there.
  const goBackLobby = useCallback(() => {
    const dest = state.history?.[state.history.length - 1] || startLobbyId;
    dispatch({ type: 'GO_BACK' });
    stageAndPreloadLobby(dest).then(() => {
      goingBackRef.current = true;
      setLobbyFading(true);
    });
  }, [state.history, startLobbyId, stageAndPreloadLobby]);

  // Show back button only when there is somewhere to go:
  //   - carousel: not on first slide
  //   - lobby: back feature is enabled (booth or showBackButton) AND
  //     there is history OR we are not already on the start lobby
  const hasBackHistory   = (state.history?.length ?? 0) > 0;
  const canGoBack        = isBoothLobby || !!lobbyHeaderConfig?.showBackButton;
  const showBackButton   = isOnNonFirstSlide || (canGoBack && (hasBackHistory || !isOnStartLobby));

  const handleBack = !showBackButton
    ? null
    : isOnNonFirstSlide
      ? () => navigateToSlide(0, 'left')
      : goBackLobby;

  const headerHeightLogical = lobbyHeaderConfig
    ? (lobbyHeaderConfig.template || 'classic') === 'pill'
      ? ((lobbyHeaderConfig.style?.pillTop ?? 16) + 8) * 2 + 56
      : (lobbyHeaderConfig.style?.height || 72)
    : 0;
  const headerHeight = Math.round(headerHeightLogical * canvasScale);

  // ── Livestream fullscreen ─────────────────────────────────────────────────
  const [livestreamFullscreen, setLivestreamFullscreen] = useState(false);

  // For carousel lobbies, stream/chat elements live in the active slide's
  // elements array (normalized via resolvedHotspots). Use resolvedHotspots
  // so both hotspots and elements are searched with their type normalized.
  const streamHotspot   = resolvedHotspots.find(h => h.hotspotType === 'stream');
  const chatHotspot     = resolvedHotspots.find(h => h.hotspotType === 'livestream-chat');
  const hasStream       = !!streamHotspot;

  // Chat panel real-pixel width: meta.width is a % of canvas width
  const chatWidthPct    = chatHotspot?.meta?.width ?? 22;
  const chatSide        = chatHotspot?.meta?.side  || 'right';
  // wrapperRef gives us the real browser dimensions
  const [wrapperW, setWrapperW] = useState(0);
  const [wrapperH, setWrapperH] = useState(0);
  useEffect(() => {
    if (!wrapperRef.current) return;
    const ro = new ResizeObserver(([e]) => {
      setWrapperW(e.contentRect.width);
      setWrapperH(e.contentRect.height);
    });
    ro.observe(wrapperRef.current);
    setWrapperW(wrapperRef.current.clientWidth);
    setWrapperH(wrapperRef.current.clientHeight);
    return () => ro.disconnect();
  }, []); // eslint-disable-line

  const chatPxWidth     = chatHotspot ? Math.round(wrapperW * (chatWidthPct / 100)) : 0;
  // Stream takes whatever is left
  const streamPxWidth   = wrapperW - chatPxWidth;

  // Derived flags
  const showGlobalLoader = preloadPhase !== 'done' && preloadPhase !== 'error';
  const showCanvas       = preloadPhase === 'done' && canvasReady;
 
  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Box
      ref={wrapperRef}
      className={className}
      style={style}
      sx={{ position: 'relative', width: '100%', height: '100%', background: '#0a0e1a', overflow: 'hidden' }}
    >
      {/* ── Global loading screen ── */}
      {(showGlobalLoader || preloadPhase === 'done') && (
        <GlobalLoadingScreen
          logoUrl={eventLogo}
          progress={preloadProgress}
          visible={showGlobalLoader}
        />
      )}
 
      {/* ── Pre-load error ── */}
      {preloadPhase === 'error' && (
        <Box sx={{ position: 'absolute', inset: 0, zIndex: 9001, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1, background: '#0a0e1a' }}>
          <Typography sx={{ color: '#fff', fontWeight: 700 }}>Failed to load event</Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.8rem' }}>{preloadError}</Typography>
        </Box>
      )}
 
      {/* ── Canvas (shown once preload is done) ── */}
      {showCanvas && (
        <>
        <Box
          ref={canvasBoxRef}
          onMouseDown={e => { if (isCarousel && slides.length > 1) e.preventDefault(); onSlideDragStart(e.clientX); }}
          onMouseMove={e => onSlideDragMove(e.clientX)}
          onMouseUp={e => onSlideDragEnd(e.clientX)}
          onMouseLeave={e => onSlideDragEnd(e.clientX)}
          onTouchStart={e => { if (isCarousel && slides.length > 1) e.preventDefault(); onSlideDragStart(e.touches[0].clientX); }}
          onTouchMove={e => { e.preventDefault(); onSlideDragMove(e.touches[0].clientX); }}
          onTouchEnd={e => onSlideDragEnd(e.changedTouches[0].clientX)}
          sx={{
            position: 'absolute',
            top:    containSize.top,
            left:   containSize.left,
            width:  containSize.width,
            height: containSize.height,
            overflow: 'hidden',
            userSelect: 'none',
            WebkitUserSelect: 'none',
            cursor: isCarousel && slides.length > 1 ? 'grab' : 'default',
          }}
        >
          <style>{`@keyframes eng-bar-slide { 0%{margin-left:0%;width:30%} 50%{margin-left:45%;width:40%} 100%{margin-left:100%;width:10%} } @keyframes ls-overlay-in { from { opacity: 0 } to { opacity: 1 } }`}</style>

          {/* Non-carousel: static background */}
          {!isCarousel && effectiveBg.bgUrl && status !== 'loading' && status !== 'error' && (
            effectiveBg.bgType === 'video' ? (
              <video key={`${lobby._id}-${engBp}`} src={effectiveBg.bgUrl} autoPlay loop muted playsInline
                onCanPlay={() => setBgLoaded(true)}
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', zIndex: 0, opacity: bgLoaded ? 1 : 0, transition: 'opacity 0.4s ease' }} />
            ) : (
              <Box key={`${lobby._id}-${engBp}`} component="img" src={effectiveBg.bgUrl} alt={lobby.name}
                onLoad={() => setBgLoaded(true)}
                ref={el => { if (el && el.complete && el.naturalWidth > 0) setBgLoaded(true); }}
                draggable={false}
                sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', zIndex: 0, opacity: bgLoaded ? 1 : 0, transition: 'opacity 0.5s ease' }} />
            )
          )}
          {!isCarousel && (status === 'visible' || status === 'transitioning') && !bgLoaded && effectiveBg.bgType === 'video' && (
            <Box sx={{ position: 'absolute', inset: 0, zIndex: 0, background: '#0a0e1a' }} />
          )}

          {/* Carousel: permanent per-slide stack. Every slide is always mounted.
             The outer container shifts so the active slide is centred (translateX = -activeSlideIndex * 100%).
             During drag/transition the transform is driven live by slideTrackRef. */}
          {isCarousel && bgLoaded && status === 'visible' && !activeTransitionUrl && (() => {
            const n = slides.length || 1;
            const makeSlide = (slide, idx) => {
              if (!slide) return null;
              const bg = resolveSlideBg(slide);
              const hs = ((slide.hotspots || []).concat(slide.elements || []))
                .map(h => resolveEngHotspot(h, engBp));
              const isActive = idx === activeSlideIndex;
              return (
                <Box key={slide.id || idx} sx={{
                  position: 'absolute', top: 0, left: `${(idx / n) * 100}%`, width: `${100 / n}%`, height: '100%',
                  overflow: 'hidden',
                }}>
                  {/* Background */}
                  {bg.bgUrl ? (
                    bg.bgType === 'video'
                      ? <video src={bg.bgUrl} autoPlay loop muted playsInline
                          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', zIndex: 0 }} />
                      : <Box component="img" src={bg.bgUrl} alt={slide.label || ''} draggable={false}
                          sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', zIndex: 0 }} />
                  ) : (
                    <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, #1a2540 0%, #2d4a6b 50%, #1a3050 100%)', zIndex: 0 }} />
                  )}
                  {/* Elements */}
                  <Box sx={{ position: 'absolute', inset: 0, zIndex: 1, pointerEvents: isActive ? 'all' : 'none' }}>
                    {hs.filter(h => h.hotspotType === 'exhibitor-grid').map(h => (
                      <EngExhibitorGrid key={h.id} hotspot={h}
                        onNavigate={navigateToLobby}
                        onNavigateSlide={slideId => {
                          const si = slides.findIndex(s => s.id === slideId);
                          if (si >= 0 && si !== activeSlideIndex) navigateToSlide(si, si > activeSlideIndex ? 'right' : 'left');
                        }}
                        onCommitLobby={commitStagedLobby}
                        canvasScale={canvasScale}
                        canvasRef={canvasBoxRef}
                        bp={engBp}
                      />
                    ))}
                    {hs.filter(h => h.hotspotType === 'text').map(h => (
                      <EngTextElement key={h.id} hotspot={h} canvasScale={canvasScale} bpCanvasW={bpCanvasW} />
                    ))}
                    {hs.filter(h => h.hotspotType === 'image').map(h => (
                      <EngImageElement key={h.id} hotspot={h} canvasScale={canvasScale} onHotspotClick={isActive ? handleHotspotClick : undefined} />
                    ))}
                    <HotspotSVGLayer
                      hotspots={hs.filter(h => h.hotspotType !== 'stream' && h.hotspotType !== 'livestream-chat' && h.hotspotType !== 'exhibitor-grid' && h.hotspotType !== 'text' && h.hotspotType !== 'image')}
                      isClickable={isActive ? isClickable : () => false}
                      onHotspotClick={isActive ? handleHotspotClick : () => {}}
                      bpCanvasW={bpCanvasW}
                      bpCanvasH={bpCanvasH}
                    />
                  </Box>
                </Box>
              );
            };
            return (
              <Box sx={{ position: 'absolute', inset: 0, zIndex: 2, overflow: 'hidden' }}>
                <Box ref={slideBgTrackRef} sx={{
                  position: 'absolute', top: 0, left: 0, height: '100%',
                  width: `${n * 100}%`,
                  transform: `translateX(${-(activeSlideIndex / n) * 100}%)`,
                  transition: slideTransition ? 'transform 0.38s cubic-bezier(0.4,0,0.2,1)' : 'none',
                  willChange: 'transform',
                }}>
                  {slides.map((slide, idx) => makeSlide(slide, idx))}
                </Box>
              </Box>
            );
          })()}

          {/* Stream and chat overlays: never slide */}
          {lobby && bgLoaded && status === 'visible' && !activeTransitionUrl && (
            <>
              {/* Dark overlay behind stream/chat/header when expanded */}
              {livestreamFullscreen && (
                <Box sx={{
                  position: 'absolute', inset: 0,
                  background: 'rgba(0,0,0,0.88)',
                  zIndex: 4,
                  animation: 'ls-overlay-in 0.35s ease forwards',
                  pointerEvents: 'none',
                }} />
              )}

              {/* Stream + Chat: geometry overrides differ by breakpoint when expanded. */}
              {/* Desktop:           stream fills left-to-chat, header-to-bottom.      */}
              {/* Mobile/tab-portrait: stream full-width 16:9 below header;            */}
              {/*                    chat full-width below stream to bottom.           */}
              {(() => {
                const isMobileLayout = livestreamFullscreen && (isMobile || engBp === 'tablet-portrait');
                const isDesktopLayout = livestreamFullscreen && !isMobileLayout;

                // Desktop expanded geometry
                const hdrPct  = (headerHeight / (wrapperH || 1)) * 100;
                const chatPct = (chatPxWidth  / (wrapperW || 1)) * 100;

                // Mobile/tablet-portrait expanded geometry
                // Stream: full width, 16:9 height starting below header
                const streamPxH    = Math.round(wrapperW * (9 / 16));
                const streamHPct   = (streamPxH   / (wrapperH || 1)) * 100;
                const chatTopPx    = headerHeight + streamPxH;

                const streamHotspots = resolvedHotspots.filter(h => h.hotspotType === 'stream');
                const chatHotspots   = resolvedHotspots.filter(h => h.hotspotType === 'livestream-chat');

                return (
                  <>
                    {streamHotspots.map(h => {
                      let hs = h;
                      if (isDesktopLayout) {
                        hs = { ...h,
                          points: [{ x: chatHotspot && chatSide === 'left' ? chatPct : 0, y: hdrPct }],
                          meta:   { ...h.meta, width: chatHotspot ? 100 - chatPct : 100, height: 100 - hdrPct },
                        };
                      } else if (isMobileLayout) {
                        hs = { ...h,
                          points: [{ x: 0, y: hdrPct }],
                          meta:   { ...h.meta, width: 100, height: streamHPct },
                        };
                      }
                      return <EngStreamElement key={h.id} hotspot={hs} />;
                    })}
                    {/* On mobile/tablet-portrait hide chat unless livestream is expanded */}
                    {(!(isMobile || engBp === 'tablet-portrait') || livestreamFullscreen) && chatHotspots.map(h => {
                      // In mobile layout override chat to full-width below the stream
                      const chatHs = isMobileLayout
                        ? { ...h, meta: { ...h.meta, width: 100, side: 'right' } }
                        : h;
                      const chatHdrHeight = isMobileLayout ? chatTopPx : headerHeight;
                      return (
                        <EngLivestreamChat
                          key={h.id}
                          hotspot={chatHs}
                          participantName={participantName || previewAuthorName}
                          participantId={anonId.current}
                          headerHeight={chatHdrHeight}
                        />
                      );
                    })}
                  </>
                );
              })()}
              {!isCarousel && (
                <>
                  {resolvedHotspots.filter(h => h.hotspotType === 'exhibitor-grid').map(h => (
                    <EngExhibitorGrid key={h.id} hotspot={h} onNavigate={navigateToLobby} onNavigateSlide={slideId => {
                      const idx = slides.findIndex(s => s.id === slideId);
                      if (idx >= 0 && idx !== activeSlideIndex) navigateToSlide(idx, idx > activeSlideIndex ? 'right' : 'left');
                    }} onCommitLobby={commitStagedLobby} canvasScale={canvasScale} canvasRef={canvasBoxRef} bp={engBp} />
                  ))}
                  {resolvedHotspots.filter(h => h.hotspotType === 'text').map(h => (
                    <EngTextElement key={h.id} hotspot={h} canvasScale={canvasScale} bpCanvasW={bpCanvasW} />
                  ))}
                  {resolvedHotspots.filter(h => h.hotspotType === 'image').map(h => (
                    <EngImageElement key={h.id} hotspot={h} canvasScale={canvasScale} onHotspotClick={handleHotspotClick} />
                  ))}
                  <HotspotSVGLayer
                    hotspots={resolvedHotspots.filter(h => h.hotspotType !== 'stream' && h.hotspotType !== 'livestream-chat' && h.hotspotType !== 'exhibitor-grid' && h.hotspotType !== 'text' && h.hotspotType !== 'image')}
                    isClickable={isClickable}
                    onHotspotClick={handleHotspotClick}
                    bpCanvasW={bpCanvasW}
                    bpCanvasH={bpCanvasH}
                  />
                </>
              )}
            </>
          )}

          {/* Carousel: left/right navigation arrows */}
          {isCarousel && slides.length > 1 && !activeSlide?.hideNav && status === 'visible' && (
            <>
              {activeSlideIndex > 0 && (
                <Box
                  onClick={e => { e.stopPropagation(); navigateToSlide(activeSlideIndex - 1, 'left'); }}
                  sx={{
                    position: 'absolute', left: '2%', top: '50%', transform: 'translateY(-50%)',
                    zIndex: 16, width: 45, height: 45, borderRadius: '50%',
                    background: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(4px)',
                    border: '1px solid rgba(255,255,255,0.15)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer', color: '#fff', userSelect: 'none',
                    transition: 'all 0.15s',
                    '&:hover': { background: 'rgba(15,23,42,0.82)', borderColor: 'rgba(255,255,255,0.35)', transform: 'translateY(-50%) scale(1.08)' },
                  }}
                >
                  <Box component="span" sx={{ fontSize: 25, lineHeight: 1, mt: '-1px' }}>‹</Box>
                </Box>
              )}
              {activeSlideIndex < slides.length - 1 && (
                <Box
                  onClick={e => { e.stopPropagation(); navigateToSlide(activeSlideIndex + 1, 'right'); }}
                  sx={{
                    position: 'absolute', right: '2%', top: '50%', transform: 'translateY(-50%)',
                    zIndex: 16, width: 45, height: 45, borderRadius: '50%',
                    background: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(4px)',
                    border: '1px solid rgba(255,255,255,0.15)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer', color: '#fff', userSelect: 'none',
                    transition: 'all 0.15s',
                    '&:hover': { background: 'rgba(15,23,42,0.82)', borderColor: 'rgba(255,255,255,0.35)', transform: 'translateY(-50%) scale(1.08)' },
                  }}
                >
                  <Box component="span" sx={{ fontSize: 25, lineHeight: 1, mt: '-1px' }}>›</Box>
                </Box>
              )}
            </>
          )}

          {/* Carousel: dot navigation bar */}
          {isCarousel && slides.length > 1 && !activeSlide?.hideNav && status === 'visible' && (
            <Box
              sx={{
                position: 'absolute', bottom: '3%', left: '50%', transform: 'translateX(-50%)',
                zIndex: 16, display: 'flex', alignItems: 'center', gap: '7.5px',
                px: 1.5, py: '6px', borderRadius: '20px',
                background: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(6px)',
                border: '1px solid rgba(255,255,255,0.12)',
                pointerEvents: 'all',
              }}
            >
              {slides.map((slide, idx) => {
                const isActive = slideTransition ? idx === slideTransition.toIndex : idx === activeSlideIndex;
                return (
                  <Box
                    key={slide.id || idx}
                    onClick={() => { if (idx !== activeSlideIndex && !slideTransition) navigateToSlide(idx, idx > activeSlideIndex ? 'right' : 'left'); }}
                    sx={{
                      width: isActive ? 25 : 7.5, height: 7.5, borderRadius: '4px',
                      background: isActive ? 'rgba(255,255,255,1)' : 'rgba(255,255,255,0.4)',
                      cursor: 'pointer', transition: 'all 0.25s', flexShrink: 0,
                      '&:hover': { background: 'rgba(255,255,255,0.9)' },
                    }}
                  />
                );
              })}
            </Box>
          )}

          {lobbyHeaderConfig && lobby && !isMobile && (
            <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 10000, pointerEvents: 'all' }}>
              <EngineHeader
                config={lobbyHeaderConfig}
                participantName={participantName || previewAuthorName}
                onLogout={onLogout}
                currentLobbyId={lobby?._id}
                chatHotspots={resolvedHotspots.filter(h => h.hotspotType === 'chat' && h.hotspotAction?.channelId)}
                onOpenChat={openChatWindow}
                onNavigateLobby={lobbyId => navigateToLobby(lobbyId)}
                isBoothLobby={isBoothLobby || isOnNonFirstSlide}
                backLabel={backLabel}
                onBack={handleBack}
                isMobile={false}
                onOpenDrawer={() => setDrawerOpen(true)}
                onLogoClick={() => navigateToLobby(startLobbyId)}
                defaultLogoSrc={state.event?.layout?.header?.logoSrc || ''}
              />
            </Box>
          )}
 
          {status === 'intro' && introVideoUrl && <IntroOverlay videoUrl={introVideoUrl} onDone={handleIntroDone} />}
 
          {activeTransitionUrl && (
            <TransitionOverlay
              videoUrl={activeTransitionUrl}
              onFadedIn={() => pendingLobbyDataRef.current?.onCommit?.()}
              onDone={handleTransitionDone}
            />
          )}
 
          {activeModal?.type === 'video'   && <VideoModal   hotspot={activeModal.hotspot} onClose={closeModal} />}
          {activeModal?.type === 'iframe'  && <IframeModal  hotspot={activeModal.hotspot} onClose={closeModal} />}
          {activeModal?.type === 'gallery' && <GalleryModal hotspot={activeModal.hotspot} onClose={closeModal} />}
 
          {/* Lobby-to-lobby crossfade */}
          {lobbyFading && (
            <LobbyFadeOverlay
              onCommit={commitStagedLobby}
              onDone={handleLobbyFadeDone}
            />
          )}
 
          <FloatingChatManager
            openChats={openChats}
            minimized={minimizedChats}
            onClose={closeChatWindow}
            onMinimize={toggleMinimize}
            authorName={previewAuthorName}
            participantId={anonId.current}
          />
        </Box>
        </>
      )}

      {/* ── Mobile header — sits at the very top of the wrapper (outside the scaled canvas) ── */}
      {/* On mobile the header must be at screen-top, not scaled inside the canvas box.        */}
      {lobbyHeaderConfig && lobby && isMobile && (
        <Box ref={mobileHeaderRef} sx={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 10000, pointerEvents: 'all' }}>
          <EngineHeader
            config={lobbyHeaderConfig}
            participantName={participantName || previewAuthorName}
            onLogout={onLogout}
            currentLobbyId={lobby?._id}
            chatHotspots={resolvedHotspots.filter(h => h.hotspotType === 'chat' && h.hotspotAction?.channelId)}
            onOpenChat={openChatWindow}
            onNavigateLobby={lobbyId => navigateToLobby(lobbyId)}
            isBoothLobby={isBoothLobby || isOnNonFirstSlide}
            backLabel={backLabel}
            onBack={handleBack}
            isMobile={true}
            onOpenDrawer={() => setDrawerOpen(true)}
            onLogoClick={() => navigateToLobby(startLobbyId)}
            defaultLogoSrc={state.event?.layout?.header?.logoSrc || ''}
          />
        </Box>
      )}

      {/* ── Mobile drawer overlay — position:absolute inside wrapperRef ── */}
      {/* Rendered here (not inside canvasBoxRef) so it isn't clipped by overflow:hidden */}
      {lobbyHeaderConfig && lobby && (
        <EngMobileDrawerOverlay
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          config={lobbyHeaderConfig}
          participantName={participantName || previewAuthorName}
          onLogout={onLogout}
          currentLobbyId={lobby?._id}
          chatHotspots={resolvedHotspots.filter(h => h.hotspotType === 'chat' && h.hotspotAction?.channelId)}
          onOpenChat={openChatWindow}
          onNavigateLobby={lobbyId => { navigateToLobby(lobbyId); setDrawerOpen(false); }}
          onLogoClick={() => { navigateToLobby(startLobbyId); setDrawerOpen(false); }}
          defaultLogoSrc={state.event?.layout?.header?.logoSrc || ''}
        />
      )}

      {/* ── Exit Livestream button (shown when expanded) ── */}
      {showCanvas && hasStream && livestreamFullscreen && status === 'visible' && (
        <Box
          onClick={() => setLivestreamFullscreen(false)}
          sx={{
            position: 'absolute',
            top: headerHeight + 12,
            // Mobile/tablet-portrait: stream is full-width so just offset 12px from right edge
            // Desktop: pull in past the chat panel on the right
            right: (!isMobile && engBp !== 'tablet-portrait' && chatHotspot && chatSide === 'right')
              ? chatPxWidth + 90
              : 12,
            zIndex: 10002,
            display: 'flex', alignItems: 'center', gap: 0.75,
            px: 2, py: 0.85,
            borderRadius: '999px',
            background: 'rgba(0,0,0,0.55)',
            border: '1px solid rgba(255,255,255,0.18)',
            backdropFilter: 'blur(10px)',
            color: '#fff',
            fontWeight: 600,
            fontSize: '0.78rem',
            cursor: 'pointer',
            pointerEvents: 'all',
            transition: 'background 0.15s',
            '&:hover': { background: 'rgba(239,68,68,0.7)', borderColor: 'rgba(239,68,68,0.4)' },
          }}
        >
          ✕ Exit Livestream
        </Box>
      )}

      {/* ── Enter Livestream button ── */}
      {showCanvas && hasStream && !livestreamFullscreen && status === 'visible' && (
        <Box
          onClick={() => setLivestreamFullscreen(true)}
          sx={{
            position: 'absolute',
            bottom: 40,
            // On mobile/tablet-portrait: stretch nearly full width so text fits on one line
            ...(isMobile
              ? { left: 16, right: 16, justifyContent: 'center' }
              : { left: '50%', transform: 'translateX(-50%)' }
            ),
            zIndex: 10001,
            display: 'flex', alignItems: 'center', gap: 1.25,
            px: 3.5, py: 1.4,
            borderRadius: '999px',
            whiteSpace: 'nowrap',
            background: 'rgba(10,14,26,0.75)',
            border: '1.5px solid rgba(255,255,255,0.18)',
            backdropFilter: 'blur(12px)',
            color: '#fff',
            fontWeight: 700,
            fontSize: '0.95rem',
            letterSpacing: '0.01em',
            cursor: 'pointer',
            boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
            transition: 'background 0.18s, border-color 0.18s, box-shadow 0.18s',
            pointerEvents: 'all',
            '&:hover': {
              background: 'rgba(239,68,68,0.85)',
              borderColor: 'rgba(239,68,68,0.6)',
              boxShadow: '0 8px 40px rgba(239,68,68,0.35)',
            },
          }}
        >
          <Box sx={{ width: 10, height: 10, borderRadius: '50%', background: '#ef4444', boxShadow: '0 0 0 3px rgba(239,68,68,0.25)', flexShrink: 0 }} />
          Enter Livestream
        </Box>
      )}

    </Box>
  );
}
 

export default LobbyEngine;